import React, { useState, ChangeEvent, FocusEventHandler, useEffect, MouseEvent } from 'react';
import {
  IconButton,
  IconButtonProps,
  Paper,
  Popover,
  PopoverProps,
  TextField,
  TextFieldProps,
} from '@material-ui/core';
import { format as formatDate } from 'date-fns';
import { DatePicker, DatePickerProps } from '@material-ui/pickers';
import { InsertInvitation } from '@material-ui/icons';
import { parseDate, isValidDate } from '../utils';

export type DateFieldProps = Omit<TextFieldProps, 'onChange'> & {
  format?: string;
  value?: Date | null;
  onChange?: (date: Date | null) => void;
  DatePickerProps?: Partial<DatePickerProps>;
  PopoverProps?: Partial<PopoverProps>;
  KeyboardButtonProps?: IconButtonProps;
  forceUpdate?: boolean;
};

const DateField = ({
  format = 'MM/dd/yyyy',
  value = null,
  onChange = () => undefined,
  onBlur = () => undefined,
  DatePickerProps = {},
  PopoverProps = {},
  KeyboardButtonProps = {},
  InputProps,
  forceUpdate = false,
  ...props
}: DateFieldProps): JSX.Element => {
  const [inputValue, setInputValue] = useState(value && isValidDate(value) ? formatDate(value, format) : '');
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  useEffect(() => {
    const newDate = new Date(value || '');

    /**
     * NOTE: using forceUpdate seems like a code smell as this allows consumers to bypass this condition. This allows consumers of the OverrideInput + DateField to
     * update the inputValue even if there is aleady an inputValue that exists. This also maintains the functionality of being able to type into the DateField input
     * freely without the date auto-formatting to a full MM/dd/yyyy. We may want to revisit this in a future implementation.
     *  */
    if (isValidDate(newDate) && (!inputValue || forceUpdate)) {
      setInputValue(formatDate(newDate, format));
    }
  }, [value]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
    const parsedDate = parseDate(event.target.value, format);
    onChange(parsedDate);
  };

  const handlePickerChange = (date: Date | null) => {
    setInputValue(formatDate(date as Date, format));
    onChange(date);
    handlePickerClose();
  };

  const handlePickerClose = () => {
    setAnchorEl(null);
  };

  const handlePickerOpen = (event: MouseEvent<HTMLButtonElement>) => {
    !anchorEl && setAnchorEl(event.currentTarget);
  };

  const handleBlur: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement> = (event) => {
    if (value && isValidDate(value)) {
      setInputValue(formatDate(value, format));
    }
    onBlur(event);
  };

  return (
    <>
      <TextField
        autoComplete="off"
        value={inputValue}
        onBlur={handleBlur}
        onChange={handleChange}
        data-testid="datefield-input"
        {...props}
        InputProps={{
          endAdornment: (
            <>
              {InputProps?.endAdornment}
              <IconButton size="small" onClick={handlePickerOpen} {...KeyboardButtonProps} style={{ bottom: 4 }}>
                <InsertInvitation fontSize="small" data-testid="datefield-calendar-button" />
              </IconButton>
            </>
          ),
        }}
      />
      {!!anchorEl && (
        <Popover
          open
          anchorEl={anchorEl}
          onClose={handlePickerClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          data-testid="datefield-calendar-popover"
          {...PopoverProps}
        >
          <Paper>
            <DatePicker
              allowKeyboardControl
              autoFocus
              value={value}
              variant="static"
              minDate={new Date(1000, 0)}
              {...DatePickerProps}
              onChange={handlePickerChange}
              onBlur={handleBlur}
              initialFocusedDate={new Date()}
            />
          </Paper>
        </Popover>
      )}
    </>
  );
};

export default DateField;
